import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import useInactivityTimer from '../hooks/useInactivityTimer';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  // Get user's current location using Geolocation API
  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      try {
        // Check if geolocation is supported
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
          console.log('Geolocation is not supported by this browser.');
          resolve(null);
          return;
        }

        const options = {
          enableHighAccuracy: true, // Use GPS if available
          timeout: 8000, // 8 seconds timeout (reduced for mobile)
          maximumAge: 60000, // Accept cached location up to 1 minute old
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            try {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp,
              });
            } catch (err) {
              console.log('Error processing location data:', err);
              resolve(null);
            }
          },
          (error) => {
            // Silently handle location errors - don't block login
            console.log('Location access failed:', error.message || 'Unknown error');
            resolve(null);
          },
          options
        );
      } catch (err) {
        // Catch any unexpected errors
        console.log('Error in getCurrentLocation:', err);
        resolve(null);
      }
    });
  };

  const login = async (username, password) => {
    try {
      // Try to get GPS location (non-blocking - login continues even if location fails)
      // Use Promise.race to ensure location request doesn't block login for too long
      let locationData = null;
      try {
        const locationPromise = getCurrentLocation();
        const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 5000)); // 5 second max wait
        locationData = await Promise.race([locationPromise, timeoutPromise]);
      } catch (locationError) {
        // Silently handle location errors - don't block login
        console.log('Location access failed, continuing with login');
      }

      // Prepare login request with location data if available
      const loginData = {
        username,
        password,
      };

      // Only add location data if we successfully got it
      if (locationData && locationData.latitude && locationData.longitude) {
        loginData.latitude = locationData.latitude;
        loginData.longitude = locationData.longitude;
        if (locationData.accuracy) {
          loginData.locationAccuracy = locationData.accuracy;
        }
      }

      const response = await api.post('/auth/login', loginData);
      const { token, ...userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed. Please check your credentials.';
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    // Navigate to login page
    window.location.href = '/login';
  }, []);

  // Handle inactivity timeout - automatically log out after 5 minutes of inactivity
  const handleInactivity = useCallback(() => {
    if (user) {
      // Store logout message in sessionStorage to show after redirect
      sessionStorage.setItem('logoutReason', 'Your session has expired due to inactivity. Please log in again.');
      logout();
    }
  }, [user, logout]);

  // Activate inactivity timer only when user is logged in
  useInactivityTimer(handleInactivity, 5);

  const value = {
    user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

